import { type ActionReducerMapBuilder, isAnyOf } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import {
  isError400WithPayload,
  isSubmitOrderResponse,
  type OrderById,
  type ValidateOrderResponse,
} from '@/store/orders/state.ts';
import { stopLossOrderApi } from '@/store/orders/stopLoss/api/index.ts';
import { isStopLossOrder } from '@/store/orders/stopLoss/helpers.ts';
import type {
  StopLossOrder,
  StopLossOrderProperties,
  ValidateStopLossOrderRequest,
} from '@/store/orders/stopLoss/state.ts';

const mapProperties = (
  currentOrder: StopLossOrder,
  originalArgs: ValidateStopLossOrderRequest,
  payload?: ValidateOrderResponse,
): StopLossOrderProperties => {
  const currentTime = Date.now();
  const clockOffset =
    payload?.backendTime !== null && payload?.backendTime !== 0 && typeof payload?.backendTime === 'number'
      ? payload?.backendTime - currentTime
      : undefined;

  return {
    status: currentOrder.properties.status,
    isReadyToSubmit: Boolean(payload?.isReadyToSubmit),
    mode: currentOrder.properties.mode,
    clockOffset,
    trigger: originalArgs.trigger ?? currentOrder.properties.trigger,

    currencyPair: {
      currency1: originalArgs.currency1,
      currency2: originalArgs.currency2,
      tiering: currentOrder.properties.currencyPair?.tiering,
    },

    clientWay: originalArgs.way ?? currentOrder.properties.clientWay,

    notional: originalArgs.notional ?? currentOrder.properties.notional,
    notionalCurrencyIndex: originalArgs.notionalCurrencyIndex ?? currentOrder.properties.notionalCurrencyIndex,

    limitPrice: originalArgs.limitPrice ?? payload?.limitPrice ?? undefined,

    expiry: originalArgs.expiry,

    notificationEmails: originalArgs.notificationEmails?.isOverridenEmails
      ? originalArgs.notificationEmails.emails
      : currentOrder.properties.notificationEmails,
  };
};

export const validateStopLossFulfilledExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(stopLossOrderApi.endpoints.validateStopLoss.matchFulfilled, (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];

    if (isNotDefined(currentOrder) || !isStopLossOrder(currentOrder)) {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    if (isNotDefined(action.meta.arg.originalArgs.currency1) || isNotDefined(action.meta.arg.originalArgs.currency2)) {
      return state;
    }

    state[tileId] = {
      ...currentOrder,

      type: 'StopLossOrder',
      clientId: currentOrder.clientId,

      properties: {
        ...mapProperties(currentOrder, action.meta.arg.originalArgs, action.payload),
        errors: {},
      },
    };
  });

export const validateStopLossPendingExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(isAnyOf(stopLossOrderApi.endpoints.validateStopLoss.matchPending), (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;
    const currentOrder = state[tileId];
    if (isNotDefined(currentOrder) || !isStopLossOrder(currentOrder)) {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    state[tileId] = {
      ...currentOrder,
      properties: {
        ...currentOrder.properties,

        trigger:
          'trigger' in action.meta.arg.originalArgs
            ? action.meta.arg.originalArgs.trigger
            : currentOrder.properties.trigger,

        clientWay: action.meta.arg.originalArgs.way ?? currentOrder.properties.clientWay,
        notional: action.meta.arg.originalArgs.notional,
        notionalCurrencyIndex: action.meta.arg.originalArgs.notionalCurrencyIndex,

        limitPrice: action.meta.arg.originalArgs.limitPrice ?? undefined,

        expiry:
          'expiry' in action.meta.arg.originalArgs
            ? action.meta.arg.originalArgs.expiry
            : currentOrder.properties.expiry,
        notificationEmails:
          action.meta.arg.originalArgs.notificationEmails?.emails ?? currentOrder.properties.notificationEmails,
      },
    };
  });

export const submitStopLossRejectedExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(
      stopLossOrderApi.endpoints.submitStopLoss.matchRejected,
      stopLossOrderApi.endpoints.updateStopLoss.matchRejected,
    ),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentOrder = state[tileId];
      const payload = isSubmitOrderResponse(action.payload) ? action.payload : undefined;

      if (
        isNotDefined(currentOrder) ||
        isNotDefined(payload) ||
        currentOrder.type !== 'StopLossOrder' ||
        payload.type !== 'failure'
      ) {
        return state;
      }

      const errorMessage = (() => {
        if (payload.errors.some((error) => error.errorDescription === 'timeout')) {
          return 'order.timeout';
        }
        if (payload.errors.some((error) => error.errorDescription === 'server.error')) {
          return 'order.serverError';
        }
        if (payload.errors.some((error) => error.errorDescription === 'order.unexpected')) {
          return 'order.unexpected';
        }
        return 'order.validationFailed';
      })();

      const areErrorsKnown = payload.errors.some((error) => error.field !== 'unknown');

      const errors = payload.errors.reduce(
        (acc, error) => {
          if (error.field === 'amountInCcy1' || error.field === 'amountInCcy2') {
            acc.notional = error;
          }

          if (error.field === 'base64Emails') {
            acc.notificationEmails = error;
          }

          acc[error.field] = error;
          return acc;
        },
        {} as Record<string, { errorCode: number; errorDescription: string; field: string }>,
      );

      const actionType = action.meta.arg.endpointName;

      state[tileId] = {
        ...currentOrder,
        type: 'StopLossOrder',
        properties: {
          ...currentOrder.properties,
          status: areErrorsKnown
            ? { type: actionType === 'submitStopLoss' ? 'creating' : 'submitted' }
            : { type: 'failed', errorMessage },
          errors: areErrorsKnown ? errors : {},
        },
      } as StopLossOrder;
    },
  );

export const validateStopLossRejectedExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(stopLossOrderApi.endpoints.validateStopLoss.matchRejected, (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];
    const payload = isError400WithPayload(action.payload) ? action.payload : undefined;

    if (isNotDefined(currentOrder) || currentOrder.type !== 'StopLossOrder') {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    if (isNotDefined(payload)) {
      state[tileId] = {
        ...currentOrder,
        type: 'StopLossOrder',
        properties: {
          ...currentOrder.properties,
          status: { type: 'failed', errorMessage: 'order.unexpected' },
        },
      } as StopLossOrder;
      return;
    }

    const errors = isDefined(payload?.validationResponseDetailContent)
      ? payload.validationResponseDetailContent.reduce(
          (acc, error) => {
            if (error.field === 'amountInCcy1' || error.field === 'amountInCcy2') {
              acc.notional = error;
            }

            if (error.field === 'base64Emails') {
              acc.notificationEmails = error;
            }

            acc[error.field] = error;
            return acc;
          },
          {} as Record<string, { errorCode: number; errorDescription: string; field: string }>,
        )
      : undefined;

    state[tileId] = {
      ...currentOrder,
      type: 'StopLossOrder',
      properties: {
        ...currentOrder.properties,
        ...mapProperties(currentOrder, action.meta.arg.originalArgs, payload),
        errors,
      },
    } as StopLossOrder;
  });

export const submitStopLossFulfilledExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(
      stopLossOrderApi.endpoints.updateStopLoss.matchFulfilled,
      stopLossOrderApi.endpoints.submitStopLoss.matchFulfilled,
    ),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const currentOrder = state[tileId];
      if (isNotDefined(currentOrder) || !isStopLossOrder(currentOrder) || action.payload.type === 'failure') {
        return state;
      }

      state[tileId] = {
        ...currentOrder,
        properties: {
          ...currentOrder.properties,
          status: {
            type: 'submitted',
            tradeId: action.payload.orderId,
          },
          isReadyToSubmit: false,
        },
      };
    },
  );

export const submitStopLossPendingExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(
      stopLossOrderApi.endpoints.updateStopLoss.matchPending,
      stopLossOrderApi.endpoints.submitStopLoss.matchPending,
    ),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const currentOrder = state[tileId];
      if (isNotDefined(currentOrder) || !isStopLossOrder(currentOrder)) {
        return state;
      }

      state[tileId] = {
        ...currentOrder,
        properties: {
          ...currentOrder.properties,
          status: {
            type: 'pending',
          },
        },
      };
    },
  );
